import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FiPlus,
  FiEdit2,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiX,
} from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminPortalInvestments.module.css';
import axiosClient from '../../api/axiosClient';

type Amount = string | number;
type Portal = 'A' | 'B' | 'C';

interface Investment {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  portal: Portal;
  invest_amount: Amount;
  term_months: number;
  status: string;
  kind?: 'normal' | 'starter' | 'topup';
  start_date: string;
  note: string | null;
  bct_tokens: Amount;
  paid_months?: number;
  paid_amount?: Amount;
  credited_months?: number;
  credited_amount?: Amount;
  capital_credited?: Amount;
  profit_credited?: Amount;
  legacy_capital_paid?: Amount;
  legacy_profit_paid?: Amount;
  settled_months?: number;
  total_roi_settled?: Amount;
  remaining_capital?: Amount;
  next_due_date?: string | null;
  next_due_amount?: Amount | null;
}

interface Payout {
  id: string;
  month_number: number;
  due_date: string;
  capital_amount: Amount;
  profit_amount: Amount;
  total_amount: Amount;
  is_maturity: boolean;
  status: string;
  roi_status?: 'pending' | 'credited' | 'paid';
  roi_credit_id?: string | null;
  credited_at?: string | null;
  paid_at?: string | null;
  funds_request_id?: string | null;
  is_due?: boolean;
  can_credit?: boolean;
}

interface UserOption {
  id: string;
  full_name: string;
  email: string;
}

interface InvestmentForm {
  userId: string;
  portal: Portal;
  investAmount: string;
  startDate: string;
  note: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: '#1d60f6',
  completed: '#23834b',
  cancelled: '#e2534a',
  starter: '#e8a86a',
};

const money = (value?: Amount | null) => {
  if (value === undefined || value === null || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const dateLabel = (value?: string | null) => {
  if (!value) return '—';
  const date = value.slice(0, 10);
  const parts = date.split('-');
  return parts.length === 3
    ? `${parts[2]}/${parts[1]}/${parts[0]}`
    : date;
};

const timestampLabel = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const todayInIndia = () => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const part = (type: string) =>
    parts.find((item) => item.type === type)?.value || '';

  return `${part('year')}-${part('month')}-${part('day')}`;
};

const errorMessage = (error: unknown, fallback: string) => {
  const candidate = error as {
    response?: { data?: { message?: string; error?: string } };
  };

  return candidate.response?.data?.message
    || candidate.response?.data?.error
    || fallback;
};

const payoutStatus = (payout: Payout) => {
  if (payout.roi_credit_id || payout.roi_status === 'credited') {
    return 'credited';
  }

  if (payout.roi_status === 'paid' || payout.status === 'paid') {
    return 'paid';
  }

  return 'pending';
};

const emptyForm = (): InvestmentForm => ({
  userId: '',
  portal: 'A',
  investAmount: '',
  startDate: todayInIndia(),
  note: '',
});

const AdminPortalInvestments: React.FC = () => {
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');

  const [refresh, setRefresh] = useState(0);
  const [usersRefresh, setUsersRefresh] = useState(0);
  const [search, setSearch] = useState('');
  const [portalFilter, setPortalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Investment | null>(null);
  const [form, setForm] = useState<InvestmentForm>(emptyForm);

  const [viewing, setViewing] = useState<Investment | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [roiFilter, setRoiFilter] = useState('');

  const [busy, setBusy] = useState('');
  const mutationLock = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');

    axiosClient.get('/admin/portal-investments')
      .then(({ data }) => {
        if (!Array.isArray(data.investments)) {
          throw new Error('Invalid investment response');
        }
        if (active) setInvestments(data.investments);
      })
      .catch((error: unknown) => {
        if (active) {
          setLoadError(errorMessage(error, 'Could not load investments.'));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [refresh]);

  useEffect(() => {
    let active = true;
    setUsersLoading(true);
    setUsersError('');

    axiosClient.get('/admin/users')
      .then(({ data }) => {
        if (!Array.isArray(data.users)) {
          throw new Error('Invalid users response');
        }
        if (active) setUsers(data.users);
      })
      .catch((error: unknown) => {
        if (active) {
          setUsersError(errorMessage(error, 'Could not load users.'));
        }
      })
      .finally(() => {
        if (active) setUsersLoading(false);
      });

    return () => {
      active = false;
    };
  }, [usersRefresh]);

  const viewingId = viewing?.id;

  useEffect(() => {
    if (!viewingId) return;

    let active = true;
    setPayouts([]);
    setScheduleError('');
    setScheduleLoading(true);

    axiosClient.get(`/admin/portal-investments/${viewingId}/payouts`)
      .then(({ data }) => {
        if (!Array.isArray(data.payouts)) {
          throw new Error('Invalid schedule response');
        }
        if (active) setPayouts(data.payouts);
      })
      .catch((error: unknown) => {
        if (active) {
          setScheduleError(errorMessage(error, 'Could not load ROI schedule.'));
        }
      })
      .finally(() => {
        if (active) setScheduleLoading(false);
      });

    return () => {
      active = false;
    };
  }, [viewingId, refresh]);

  const beginMutation = (key: string) => {
    if (mutationLock.current) return false;
    mutationLock.current = true;
    setBusy(key);
    return true;
  };

  const endMutation = () => {
    mutationLock.current = false;
    setBusy('');
  };

  const reload = () => setRefresh((value) => value + 1);

  const closeForm = () => {
    if (!mutationLock.current) setShowForm(false);
  };

  const closeSchedule = () => {
    if (!mutationLock.current) setViewing(null);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (investment: Investment) => {
    setEditing(investment);
    setForm({
      userId: investment.user_id,
      portal: investment.portal,
      investAmount: String(investment.invest_amount),
      startDate: investment.start_date.slice(0, 10),
      note: investment.note || '',
    });
    setShowForm(true);
  };

  const openSchedule = (investment: Investment) => {
    setPayouts([]);
    setScheduleError('');
    setScheduleLoading(true);
    setMonthFilter('');
    setRoiFilter('');
    setViewing(investment);
  };

  const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const amount = form.investAmount.trim();

    if (!editing && !form.userId) {
      toast.error('Select a user.');
      return;
    }

    if (!/^\d{1,12}(\.\d{1,2})?$/.test(amount) || Number(amount) <= 0) {
      toast.error('Enter a positive INR amount with up to two decimal places.');
      return;
    }

    if (!form.startDate) {
      toast.error('Select a start date.');
      return;
    }

    if (!beginMutation('save')) return;

    try {
      const payload = {
        portal: form.portal,
        investAmount: amount,
        startDate: form.startDate,
        note: form.note.trim(),
      };

      if (editing) {
        await axiosClient.patch(
          `/admin/portal-investments/${editing.id}`,
          payload,
        );
        toast.success('Investment updated.');
      } else {
        await axiosClient.post('/admin/portal-investments', {
          ...payload,
          userId: form.userId,
        });
        toast.success('Investment created with its ROI schedule.');
      }

      setShowForm(false);
      reload();
    } catch (error: unknown) {
      toast.error(errorMessage(error, 'Could not save investment.'));
    } finally {
      endMutation();
    }
  };

  const cancelInvestment = async (investment: Investment) => {
    if (mutationLock.current) return;

    const confirmed = window.confirm(
      `Cancel ${investment.full_name}'s Portal ${investment.portal} investment?\n\n`
      + 'Future ROI credits will stop and the locked principal is released. '
      + 'If the user paid by a real deposit, that money becomes normal withdrawable balance. '
      + 'If you opened this investment without a deposit, the wallet credit it created is taken back. '
      + 'ROI already credited stays with the user.',
    );

    if (!confirmed || !beginMutation(`cancel:${investment.id}`)) return;

    try {
      await axiosClient.patch(
        `/admin/portal-investments/${investment.id}`,
        { status: 'cancelled' },
      );
      toast.success('Investment cancelled.');
      reload();
    } catch (error: unknown) {
      toast.error(errorMessage(error, 'Could not cancel investment.'));
    } finally {
      endMutation();
    }
  };

  const creditRoi = async (payout: Payout) => {
    if (
      !viewing
      || mutationLock.current
      || loading
      || Boolean(loadError)
      || payout.can_credit !== true
      || payoutStatus(payout) !== 'pending'
    ) return;

    const confirmed = window.confirm(
      `Credit month ${payout.month_number} ROI to ${viewing.full_name}?\n\n`
      + `Capital Back: ${money(payout.capital_amount)}\n`
      + `Profit Sharing: ${money(payout.profit_amount)}\n`
      + `Total Wallet Credit: ${money(payout.total_amount)}\n\n`
      + 'The admin charge (set in System Settings) applies when the user withdraws.',
    );

    if (!confirmed || !beginMutation(`credit:${payout.id}`)) return;

    try {
      const { data } = await axiosClient.patch(
        `/admin/portal-payouts/${payout.id}/mark-paid`,
        { action: 'credit_roi' },
      );

      setPayouts((current) => current.map((item) =>
        item.id === payout.id
          ? { ...item, roi_status: 'credited', can_credit: false }
          : item,
      ));

      toast.success(
        data.alreadyCredited
          ? 'This month’s ROI was already credited.'
          : 'ROI credited to the user’s INR wallet.',
      );
      reload();
    } catch (error: unknown) {
      toast.error(errorMessage(error, 'Could not credit ROI.'));
      reload();
    } finally {
      endMutation();
    }
  };

  const filteredInvestments = investments.filter((investment) => {
    const query = search.trim().toLowerCase();
    const matchesSearch = !query
      || `${investment.full_name} ${investment.email} ${investment.id}`
        .toLowerCase().includes(query);

    return matchesSearch
      && (!portalFilter || investment.portal === portalFilter)
      && (!statusFilter || investment.status === statusFilter);
  });

  const selected = viewing
    ? investments.find((investment) => investment.id === viewing.id) || viewing
    : null;

  const filteredPayouts = payouts
    .filter((payout) =>
      (!monthFilter || payout.due_date.slice(0, 7) === monthFilter)
      && (!roiFilter || payoutStatus(payout) === roiFilter),
    )
    .sort((a, b) => a.month_number - b.month_number);

  const controlsStyle: React.CSSProperties = {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: 16,
  };

  return (
    <AdminShell>
      <PageHeader
        title="Portal Investments"
        subtitle="Manage investments, review monthly ROI and credit due returns to user wallets."
        action={
          <div style={controlsStyle}>
            <AdminWalletBadge />
            <button
              className={styles.createBtn}
              onClick={openCreate}
              disabled={Boolean(busy)}
            >
              <FiPlus /> New Investment
            </button>
          </div>
        }
      />

      <div className={panelStyles.page}>
        <div style={controlsStyle}>
          <input
            className={styles.input}
            style={{ width: 260, margin: 0 }}
            placeholder="Search name, email or investment ID"
            aria-label="Search investments"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <select
            className={styles.input}
            style={{ width: 150, margin: 0 }}
            aria-label="Filter by portal"
            value={portalFilter}
            onChange={(event) => setPortalFilter(event.target.value)}
          >
            <option value="">All portals</option>
            <option value="A">Portal A</option>
            <option value="B">Portal B</option>
            <option value="C">Portal C</option>
          </select>

          <select
            className={styles.input}
            style={{ width: 160, margin: 0 }}
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <button
            className={panelStyles.actionBtn}
            disabled={loading || Boolean(busy)}
            onClick={reload}
          >
            Refresh
          </button>
        </div>

        <div className={panelStyles.tableWrap} style={{ overflowX: 'auto' }}>
          {loading ? (
            <div className={panelStyles.loading}>
              <span className={panelStyles.spinnerLg} />
            </div>
          ) : loadError ? (
            <div className={panelStyles.empty} role="alert">
              {loadError}
              <button className={panelStyles.actionBtn} onClick={reload}>
                Retry
              </button>
            </div>
          ) : filteredInvestments.length === 0 ? (
            <div className={panelStyles.empty}>No matching investments.</div>
          ) : (
            <table className={panelStyles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Portal / Investment</th>
                  <th>Capital Back Credited</th>
                  <th>Profit Sharing Credited</th>
                  <th>Total ROI Credited</th>
                  <th>Remaining Capital</th>
                  <th>Progress</th>
                  <th>BCT100 Allocation</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvestments.map((investment) => (
                  <tr key={investment.id}>
                    <td>
                      <div className={panelStyles.nameCell}>
                        {investment.full_name}
                      </div>
                      <div className={panelStyles.subCell}>
                        {investment.email}
                      </div>
                    </td>
                    <td>
                      <div>Portal {investment.portal}{investment.kind === 'starter' ? ' · Starter' : investment.kind === 'topup' ? ' · Top-up' : ''}</div>
                      <strong>{money(investment.invest_amount)}</strong>
                      <div className={panelStyles.subCell}>
                        {dateLabel(investment.start_date)}
                      </div>
                    </td>
                    <td>{money(investment.capital_credited)}</td>
                    <td>{money(investment.profit_credited)}</td>
                    <td>
                      <strong>{money(investment.credited_amount)}</strong>
                      {Number(investment.paid_months || 0) > 0 && (
                        <div className={panelStyles.subCell}>
                          Earlier paid: {money(investment.paid_amount)}
                        </div>
                      )}
                    </td>
                    <td>{money(investment.remaining_capital)}</td>
                    <td>
                      {investment.settled_months ?? '—'}
                      /{investment.term_months} months
                      {investment.next_due_date && (
                        <div className={panelStyles.subCell}>
                          Next: {dateLabel(investment.next_due_date)}
                          <br />
                          {money(investment.next_due_amount)}
                        </div>
                      )}
                    </td>
                    <td>
                      {investment.bct_tokens != null
                        ? Number(investment.bct_tokens).toLocaleString('en-IN', {
                            maximumFractionDigits: 6,
                          })
                        : '—'}
                    </td>
                    <td>
                      <span
                        className={panelStyles.statusPill}
                        style={{
                          color: STATUS_COLORS[investment.status],
                          borderColor: STATUS_COLORS[investment.status],
                        }}
                      >
                        {investment.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          className={panelStyles.actionBtn}
                          disabled={Boolean(busy)}
                          onClick={() => openSchedule(investment)}
                        >
                          <FiCalendar /> ROI Report
                        </button>
                        <button
                          className={panelStyles.actionBtn}
                          disabled={Boolean(busy)}
                          onClick={() => openEdit(investment)}
                        >
                          <FiEdit2 /> Edit
                        </button>
                        {(investment.status === 'active' || investment.status === 'starter') && (
                          <button
                            className={panelStyles.actionBtnReject}
                            disabled={Boolean(busy)}
                            onClick={() => cancelInvestment(investment)}
                          >
                            {busy === `cancel:${investment.id}`
                              ? 'Cancelling…'
                              : 'Cancel'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className={styles.hint}>
          ROI credits are added to the user’s wallet. Withdrawals are processed
          separately with the admin charge set in System Settings. Remaining capital excludes capital
          already credited and earlier recorded capital payments.
          BCT100 allocation is not the available claim balance.
        </p>
      </div>

      {showForm && (
        <div className={styles.overlay} onClick={closeForm}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="investment-form-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 id="investment-form-title">
                {editing ? 'Edit Investment' : 'New Portal Investment'}
              </h3>
              <button
                onClick={closeForm}
                disabled={Boolean(busy)}
                aria-label="Close investment form"
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={submitForm}>
              <fieldset
                disabled={Boolean(busy)}
                style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
              >
                {!editing && (
                  <>
                    <label className={styles.label} htmlFor="investment-user">
                      User
                    </label>
                    <select
                      id="investment-user"
                      className={styles.input}
                      value={form.userId}
                      required
                      disabled={usersLoading || Boolean(usersError)}
                      onChange={(event) =>
                        setForm({ ...form, userId: event.target.value })
                      }
                    >
                      <option value="">
                        {usersLoading ? 'Loading users…' : 'Select user'}
                      </option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} — {user.email}
                        </option>
                      ))}
                    </select>
                    {usersError && (
                      <div role="alert" className={styles.hint}>
                        {usersError}{' '}
                        <button
                          type="button"
                          onClick={() => setUsersRefresh((value) => value + 1)}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                  </>
                )}

                <label className={styles.label} htmlFor="investment-portal">
                  Portal
                </label>
                <select
                  id="investment-portal"
                  className={styles.input}
                  value={form.portal}
                  onChange={(event) =>
                    setForm({ ...form, portal: event.target.value as Portal })
                  }
                >
                  <option value="A">Portal A</option>
                  <option value="B">Portal B</option>
                  <option value="C">Portal C</option>
                </select>

                <label className={styles.label} htmlFor="investment-amount">
                  Investment Amount (₹)
                </label>
                <input
                  id="investment-amount"
                  className={styles.input}
                  type="number"
                  min="0.01"
                  max="999999999999.99"
                  step="0.01"
                  required
                  disabled={Boolean(editing)}
                  title={editing ? 'The amount is already in the wallet. Cancel and create a new investment to change it.' : undefined}
                  value={form.investAmount}
                  placeholder="25000"
                  onChange={(event) =>
                    setForm({ ...form, investAmount: event.target.value })
                  }
                />

                <div style={{ fontSize: 11.5, opacity: 0.7, margin: '-4px 0 10px' }}>
                  {editing
                    ? 'The amount cannot be changed once it is in the wallet. Cancel and create a new investment instead.'
                    : 'The amount is credited to the user\'s wallet and stays locked; it comes back through the monthly ROI.'}
                </div>

                <label className={styles.label} htmlFor="investment-date">
                  Start Date
                </label>
                <input
                  id="investment-date"
                  className={styles.input}
                  type="date"
                  required
                  value={form.startDate}
                  onChange={(event) =>
                    setForm({ ...form, startDate: event.target.value })
                  }
                />

                <label className={styles.label} htmlFor="investment-note">
                  Note (optional)
                </label>
                <textarea
                  id="investment-note"
                  className={styles.input}
                  rows={3}
                  value={form.note}
                  onChange={(event) =>
                    setForm({ ...form, note: event.target.value })
                  }
                />

                <div className={styles.hint}>
                  {editing
                    ? 'Changing the portal, amount or start date regenerates the schedule. These changes are blocked after an ROI credit, linked payout or token claim. Notes can still be updated.'
                    : 'The server generates the ROI schedule using the configured portal plan. The user must have approved KYC.'}
                </div>

                <button
                  type="submit"
                  className={styles.saveBtn}
                  disabled={
                    Boolean(busy)
                    || (!editing && (usersLoading || Boolean(usersError)))
                  }
                >
                  {busy === 'save'
                    ? 'Saving…'
                    : editing ? 'Save Changes' : 'Create Investment'}
                </button>
              </fieldset>
            </form>
          </div>
        </div>
      )}

      {selected && (
        <div className={styles.overlay} onClick={closeSchedule}>
          <div
            className={styles.modal}
            style={{ maxWidth: 1100, width: '95%' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="roi-report-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 id="roi-report-title">
                {selected.full_name} — Portal {selected.portal} ROI Report
              </h3>
              <button
                onClick={closeSchedule}
                disabled={Boolean(busy)}
                aria-label="Close ROI report"
              >
                <FiX />
              </button>
            </div>

            {loading ? (
              <p className={styles.hint}>Refreshing investment totals…</p>
            ) : loadError ? (
              <p role="alert">{loadError} Please refresh before crediting ROI.</p>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: 14,
                  marginBottom: 20,
                }}
              >
                {[
                  ['Investment', money(selected.invest_amount)],
                  ['Capital Back Credited', money(selected.capital_credited)],
                  ['Profit Sharing Credited', money(selected.profit_credited)],
                  ['Total ROI Credited', money(selected.credited_amount)],
                  ['Earlier Capital Paid', money(selected.legacy_capital_paid)],
                  ['Earlier Profit Paid', money(selected.legacy_profit_paid)],
                  ['Total ROI Settled', money(selected.total_roi_settled)],
                  ['Remaining Capital', money(selected.remaining_capital)],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className={panelStyles.subCell}>{label}</div>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            )}

            <div style={controlsStyle}>
              <label>
                Due month
                <input
                  className={styles.input}
                  type="month"
                  value={monthFilter}
                  onChange={(event) => setMonthFilter(event.target.value)}
                />
              </label>

              <label>
                ROI status
                <select
                  className={styles.input}
                  value={roiFilter}
                  onChange={(event) => setRoiFilter(event.target.value)}
                >
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="credited">Wallet credited</option>
                  <option value="paid">Earlier paid</option>
                </select>
              </label>

              <button
                className={panelStyles.actionBtn}
                onClick={() => {
                  setMonthFilter('');
                  setRoiFilter('');
                }}
              >
                Clear Filters
              </button>

              <button
                className={panelStyles.actionBtn}
                disabled={scheduleLoading || loading || Boolean(busy)}
                onClick={reload}
              >
                Refresh
              </button>
            </div>

            <div className={styles.scheduleScroll} style={{ overflowX: 'auto' }}>
              {scheduleLoading ? (
                <div className={panelStyles.loading}>Loading ROI schedule…</div>
              ) : scheduleError ? (
                <div className={panelStyles.empty} role="alert">
                  {scheduleError}
                  <button className={panelStyles.actionBtn} onClick={reload}>
                    Retry
                  </button>
                </div>
              ) : filteredPayouts.length === 0 ? (
                <div className={panelStyles.empty}>No matching ROI entries.</div>
              ) : (
                <table className={styles.scheduleTable}>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Due Date</th>
                      <th>Capital Back</th>
                      <th>Profit Sharing</th>
                      <th>Total ROI Bonus</th>
                      <th>Status</th>
                      <th>Recorded At (IST)</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayouts.map((payout) => {
                      const status = payoutStatus(payout);
                      const eligible = payout.can_credit === true
                        && status === 'pending'
                        && selected.status === 'active';

                      return (
                        <tr key={payout.id}>
                          <td>
                            {payout.month_number}
                            {payout.is_maturity && (
                              <span className={styles.maturityTag}>Maturity</span>
                            )}
                          </td>
                          <td>{dateLabel(payout.due_date)}</td>
                          <td>{money(payout.capital_amount)}</td>
                          <td>{money(payout.profit_amount)}</td>
                          <td style={{ fontWeight: 700 }}>
                            {money(payout.total_amount)}
                          </td>
                          <td>
                            {status === 'credited' ? (
                              <span className={styles.paidTag}>
                                <FiCheckCircle /> Wallet credited
                              </span>
                            ) : status === 'paid' ? (
                              <span className={styles.paidTag}>
                                <FiCheckCircle /> Earlier paid
                              </span>
                            ) : (
                              <span className={styles.pendingTag}>
                                <FiClock /> Pending
                              </span>
                            )}
                          </td>
                          <td>
                            {timestampLabel(
                              status === 'credited'
                                ? payout.credited_at
                                : status === 'paid' ? payout.paid_at : null,
                            )}
                          </td>
                          <td>
                            {eligible ? (
                              <button
                                className={panelStyles.actionBtn}
                                disabled={
                                  Boolean(busy) || loading || Boolean(loadError)
                                }
                                onClick={() => creditRoi(payout)}
                              >
                                {busy === `credit:${payout.id}`
                                  ? 'Crediting…'
                                  : 'Credit ROI'}
                              </button>
                            ) : status !== 'pending' ? (
                              '—'
                            ) : (
                              <span className={panelStyles.subCell}>
                                {selected.status !== 'active'
                                  ? 'Investment inactive'
                                  : payout.funds_request_id
                                    ? 'Linked payout — review required'
                                    : payout.is_due === false
                                      ? 'Not due yet'
                                      : 'Not eligible for credit'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <p className={styles.hint}>
              Total ROI Bonus = Capital Back + Profit Sharing.
              Credit ROI adds the full amount to the user’s INR wallet.
              The admin charge (set in System Settings) is deducted when processing a withdrawal.
              Direct and override bonuses are separate from this report.
            </p>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminPortalInvestments;